import { Box, HStack } from 'native-base';
import { useEffect, useMemo, useState } from 'react';
import Select from 'ui/components/v2/inputs/Select';
import { ISelectOptionsItem } from 'ui/components/v2/inputs/Select/types';
import TextInput from 'ui/components/v2/inputs/TextInput';
import { MeasurementCategory } from 'ui/enums';
import { MeasurementUnit } from 'ui/types/measurementUnit';

import { activityMetricsOptions } from '~/pages/Authenticated/Actions/AddAction/ActionForm/AboutForm/utils';

export type ICharitiesHelpedFilters = {
  text: string;
  option: ISelectOptionsItem<{
    data: MeasurementUnit;
    group: MeasurementCategory;
  }> | null;
};

type CharitiesHelpedFiltersProps = {
  onFilterChange: (filters: ICharitiesHelpedFilters) => void;
  isMeasurementFilterEnabled?: boolean;
  measurementUnits: MeasurementUnit[];
};

export const CharitiesHelpedFilters = ({
  onFilterChange,
  isMeasurementFilterEnabled = true,
  measurementUnits: _measurementUnits,
}: CharitiesHelpedFiltersProps) => {
  const [textFilter, setTextFilter] = useState('');

  const [selectedOption, setSelectedOption] =
    useState<ICharitiesHelpedFilters['option']>(null);

  useEffect(() => {
    onFilterChange({
      text: textFilter,
      option: selectedOption,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, textFilter]);

  // Group by category and avoiding duplicate entries in each group
  const measurementUnits = useMemo(() => {
    return _measurementUnits?.reduce(
      (group: { [key: string]: MeasurementUnit[] }, measurementUnit) => {
        const { category } = measurementUnit;
        group[category] = group[category] ?? [];

        if (group[category].find((unit) => unit._id === measurementUnit._id)) {
          return group;
        }

        group[category].push(measurementUnit);
        return group;
      },
      {},
    );
  }, [_measurementUnits]);

  const selectOptions = useMemo(() => {
    const values = activityMetricsOptions(measurementUnits) || [];
    const filteredValues = values.filter(
      (item) => item.label !== MeasurementCategory.Completion,
    );

    return filteredValues;
  }, [measurementUnits]);

  useEffect(() => {
    setSelectedOption((prevState) => {
      if (prevState === null && selectOptions.length > 0) {
        return selectOptions[0].options[0] ?? null;
      }
      return prevState;
    });
  }, [selectOptions]);

  return (
    <HStack space={2} px={6}>
      <Box flex={1}>
        <TextInput.Base placeholder="Search charity" onChange={setTextFilter} />
      </Box>
      {isMeasurementFilterEnabled && (
        <Box flex={1}>
          <Select.Base
            options={selectOptions}
            value={selectedOption}
            // @ts-expect-error - valid arg
            onChange={setSelectedOption}
          />
        </Box>
      )}
    </HStack>
  );
};
